'use client'

import * as React from 'react'
import { cn } from '../../utils'

export interface SecondaryTabBarItem {
  label: string
  href: string
  /** True if this is the currently-active tab. */
  active?: boolean
  /** Optional badge (count, "new", etc). */
  badge?: React.ReactNode
  /**
   * Optional leading icon component (e.g. a lucide-react icon).
   * Rendered at 14×14 with the same color as the label.
   */
  icon?: React.ComponentType<{ className?: string }>
}

export interface SecondaryTabBarProps {
  /** Tabs to render in order. */
  tabs: SecondaryTabBarItem[]
  /**
   * Renderer for each link. Apps pass `next/link` (or any framework's
   * link component) so the bar stays framework-agnostic.
   * Defaults to a plain `<a>`.
   */
  as?: React.ElementType
  /** Optional content rendered on the right side (e.g. filter, action button). */
  right?: React.ReactNode
  className?: string
}

/**
 * The per-group sub-navigation row that sits under the top Topbar.
 * Each top-nav group renders one of these with its own tab list.
 *
 * The active tab is underlined with a heavier weight; inactive tabs are
 * muted and become full-foreground on hover.
 */
export function SecondaryTabBar({
  tabs,
  as: As = 'a',
  right,
  className,
}: SecondaryTabBarProps) {
  return (
    <div
      role="navigation"
      aria-label="Secondary navigation"
      className={cn(
        'w-full border-b border-border bg-background',
        className
      )}
    >
      <div className="flex items-center justify-between gap-4 px-6 h-10">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
            <As
              key={tab.href}
              href={tab.href}
              aria-current={tab.active ? 'page' : undefined}
              className={cn(
                // `shrink-0` keeps the tab at its natural width so the
                // parent's overflow-x-auto kicks in on narrow viewports
                // instead of compressing labels.
                'group relative inline-flex shrink-0 items-center gap-1.5 px-3 h-10 text-body whitespace-nowrap',
                'transition-colors',
                tab.active
                  ? 'text-foreground font-semibold'
                  : 'text-muted-foreground hover:text-foreground font-medium'
              )}
            >
              {Icon && <Icon className="w-3.5 h-3.5" />}
              <span>{tab.label}</span>
              {tab.badge != null && (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground text-overline font-semibold leading-none px-1.5 py-0.5">
                  {tab.badge}
                </span>
              )}
              {tab.active && (
                <span
                  aria-hidden
                  className="absolute inset-x-2 -bottom-px h-0.5 bg-foreground rounded-full"
                />
              )}
            </As>
          )})}
        </div>
        {right && <div className="flex items-center gap-2 shrink-0">{right}</div>}
      </div>
    </div>
  )
}
